import { apiClient } from '../apiClient';
import {
  UserProfile,
  CreateUserRequest,
  UpdateUserRequest,
  UserFilters,
  Organization,
  OrganizationFilters,
  SystemStatus,
  AuditLog,
  AuditLogFilters,
  PaginatedResponse,
} from '../../types';

// Login-shaped payload returned by the impersonation endpoint (mirrors /auth/login)
export interface ImpersonationResponse {
  message: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    name: string;
    user_type: string;
    organization_slug: string | null;
    org_roles?: string[];
    platform_roles?: string[];
    enabled: boolean;
  };
}

export class AdminApiService {
  // System Management
  async getSystemStatus(): Promise<SystemStatus> {
    return apiClient.get<SystemStatus>('/admin/system/status');
  }

  async getSystemHealth(): Promise<{
    status: 'healthy' | 'warning' | 'critical';
    checks: Array<{
      name: string;
      status: 'up' | 'down';
      response_time?: number;
      error?: string;
    }>;
  }> {
    return apiClient.get('/admin/system/health');
  }

  // User Management
  async getUsers(filters: UserFilters): Promise<PaginatedResponse<UserProfile>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.append(key, String(value));
      }
    });
    return apiClient.get<PaginatedResponse<UserProfile>>(`/admin/users?${params}`);
  }

  async getUser(userId: string): Promise<UserProfile> {
    return apiClient.get<UserProfile>(`/admin/users/${userId}`);
  }

  async createUser(data: CreateUserRequest): Promise<UserProfile> {
    return apiClient.post<UserProfile, CreateUserRequest>('/admin/users', data);
  }

  async updateUser(userId: string, data: UpdateUserRequest): Promise<UserProfile> {
    return apiClient.patch<UserProfile, UpdateUserRequest>(`/admin/users/${userId}`, data);
  }

  async deleteUser(userId: string): Promise<void> {
    return apiClient.delete<void>(`/admin/users/${userId}`);
  }

  async enableUser(userId: string): Promise<UserProfile> {
    return apiClient.post<UserProfile>(`/admin/users/${userId}/enable`, {});
  }

  async disableUser(userId: string): Promise<UserProfile> {
    return apiClient.post<UserProfile>(`/admin/users/${userId}/disable`, {});
  }

  async resendUserInvitation(userId: string): Promise<void> {
    return apiClient.post<void>(`/admin/users/${userId}/resend-invitation`, {});
  }

  // Impersonation ("login as") — returns a login-shaped payload for the target user
  async impersonateUser(userId: string): Promise<ImpersonationResponse> {
    return apiClient.post<ImpersonationResponse>(`/admin/users/${userId}/impersonate`, {});
  }

  async stopImpersonation(): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>('/admin/impersonation/stop', {});
  }

  // Organization Management
  async getOrganizations(filters: OrganizationFilters): Promise<PaginatedResponse<Organization>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.append(key, String(value));
      }
    });
    return apiClient.get<PaginatedResponse<Organization>>(`/admin/organizations?${params}`);
  }

  async getOrganization(organizationId: string): Promise<Organization> {
    return apiClient.get<Organization>(`/admin/organizations/${organizationId}`);
  }

  async createOrganization(data: {
    name: string;
    slug: string;
    email: string;
    subscription_tier: 'starter' | 'professional' | 'enterprise';
    owner_email: string;
    settings?: Partial<Organization['settings']>;
  }): Promise<Organization> {
    return apiClient.post<Organization>('/admin/organizations', data);
  }

  async updateOrganization(
    organizationId: string,
    data: Partial<Organization>
  ): Promise<Organization> {
    return apiClient.patch<Organization>(`/admin/organizations/${organizationId}`, data);
  }

  async deleteOrganization(organizationId: string): Promise<void> {
    return apiClient.delete<void>(`/admin/organizations/${organizationId}`);
  }

  async enableOrganization(organizationId: string): Promise<Organization> {
    return apiClient.post<Organization>(`/admin/organizations/${organizationId}/enable`, {});
  }

  async disableOrganization(organizationId: string): Promise<Organization> {
    return apiClient.post<Organization>(`/admin/organizations/${organizationId}/disable`, {});
  }

  async updateOrganizationSubscription(
    organizationId: string,
    tier: 'starter' | 'professional' | 'enterprise'
  ): Promise<Organization> {
    return apiClient.post<Organization>(`/admin/organizations/${organizationId}/subscription`, {
      subscription_tier: tier,
    });
  }

  // Audit Logs
  async getAuditLogs(filters: AuditLogFilters): Promise<PaginatedResponse<AuditLog>> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.append(key, String(value));
      }
    });
    return apiClient.get<PaginatedResponse<AuditLog>>(`/admin/audit-logs?${params}`);
  }

  async getAuditLog(auditLogId: string): Promise<AuditLog> {
    return apiClient.get<AuditLog>(`/admin/audit-logs/${auditLogId}`);
  }

  // Platform Analytics
  async getPlatformAnalytics(params: {
    date_from?: string;
    date_to?: string;
    breakdown?: 'daily' | 'weekly' | 'monthly';
  }): Promise<{
    total_organizations: number;
    total_users: number;
    total_events: number;
    total_revenue: number; // in cents
    growth_metrics: {
      organizations_growth: number;
      users_growth: number;
      revenue_growth: number;
    };
    revenue_timeline: Array<{
      date: string;
      revenue: number;
      organizations: number;
    }>;
    top_organizations: Array<{
      id: string;
      name: string;
      revenue: number;
      events: number;
    }>;
  }> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    return apiClient.get(`/admin/analytics/platform?${searchParams}`);
  }

  // Feature Flag Management
  async updateOrganizationFeatures(
    organizationId: string,
    features: Partial<import('../../types').FeatureFlags>
  ): Promise<Organization> {
    return apiClient.patch<Organization>(`/admin/organizations/${organizationId}/features`, {
      features,
    });
  }

  // Bulk Operations
  async bulkUpdateUsers(
    userIds: string[],
    updates: Partial<UpdateUserRequest>
  ): Promise<{ updated: number; errors: Array<{ id: string; error: string }> }> {
    return apiClient.post('/admin/users/bulk-update', {
      user_ids: userIds,
      updates,
    });
  }

  async bulkDeleteUsers(userIds: string[]): Promise<{
    deleted: number;
    errors: Array<{ id: string; error: string }>;
  }> {
    return apiClient.post('/admin/users/bulk-delete', {
      user_ids: userIds,
    });
  }

  async exportUsers(filters: UserFilters): Promise<{
    download_url: string;
    expires_at: string;
  }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.append(key, String(value));
      }
    });
    return apiClient.post(`/admin/users/export?${params}`, {});
  }

  async exportOrganizations(filters: OrganizationFilters): Promise<{
    download_url: string;
    expires_at: string;
  }> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.append(key, String(value));
      }
    });
    return apiClient.post(`/admin/organizations/export?${params}`, {});
  }
}

export const adminService = new AdminApiService();